/* global moment */
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Context } from "../../context/Context";
import Highcharts, { chart } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { allCharts } from "../../api/api";
import { DatePickerRange } from "../../components/flatpickr/DatePicker";
import PersianDate from "persian-date";
import ChartExport from "../exports/CharExport";
import DeviceCharts from "../../constants/DeviceCharts";
import ErrorToast from "../toasts/ErrorToast";
import "./ChartModal.css";

const ChartModal = ({ button }) => {
  const { setChartModal, setIsLoading, equipmentButtons, chartInfo, devices } =
    useContext(Context);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [chartData, setChartData] = useState([]);
  const [excelExportStatus, setExcelExportStatus] = useState(false);
  const [getChartLoading, setGetChartLoading] = useState(false);

  const modalName = useRef(null);
  const closeButton = useRef(null);
  const chartComponentRef = useRef();

  useEffect(() => {
    setChartModal(modalName);

    Highcharts.dateFormats = {
      a: (ts) => new PersianDate(ts).format("dddd"),
      A: (ts) => new PersianDate(ts).format("dddd"),
      d: (ts) => new PersianDate(ts).format("DD"),
      e: (ts) => new PersianDate(ts).format("D"),
      b: (ts) => new PersianDate(ts).format("MMMM"),
      B: (ts) => new PersianDate(ts).format("MMMM"),
      m: (ts) => new PersianDate(ts).format("MM"),
      y: (ts) => new PersianDate(ts).format("YY"),
      Y: (ts) => new PersianDate(ts).format("YYYY"),
      W: (ts) => new PersianDate(ts).format("ww"),
    };

    const applyTheme = () => {
      let theme = localStorage.getItem(
        "templateCustomizer-vertical-menu-template--Style",
      );

      const lightTheme = {
        chart: {
          backgroundColor: "#ffffff",
        },
        title: {
          style: {
            color: "#000",
          },
        },
        legend: {
          itemStyle: {
            color: "#000",
          },
        },
        xAxis: {
          labels: { style: { color: "#000" } },
          gridLineColor: "#e6e6e6",
        },
        yAxis: {
          labels: { style: { color: "#000" } },
          gridLineColor: "#e6e6e6",
        },
      };

      const darkTheme = {
        chart: {
          backgroundColor: "#283144",
        },
        title: {
          style: {
            color: "#ffffff",
          },
        },
        legend: {
          itemStyle: {
            color: "#ffffff",
          },
        },
        xAxis: {
          labels: { style: { color: "#ffffff" } },
          gridLineColor: "#444",
        },
        yAxis: {
          labels: { style: { color: "#ffffff" } },
          gridLineColor: "#444",
        },
      };

      Highcharts.setOptions(theme == "dark" ? darkTheme : lightTheme);
    };

    applyTheme();

    const observer = new MutationObserver(applyTheme);

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setChartData([]);
    setExcelExportStatus(false);
  }, [chartInfo]);

  useEffect(() => {
    if (chartComponentRef.current) {
      chartComponentRef.current.chart.update({
        series: chartData,
      });
    }
  }, [chartData]);

  const toLocalTimestamp = (dateStr) => {
    const [datePart, timePart = "00:00:00"] = dateStr.trim().split(" ");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hour, minute, second] = timePart.split(":").map(Number);
    return new Date(year, month - 1, day, hour, minute, second).getTime();
  };

  const handleShowChart = () => {
    if (![1, 2].includes(chartInfo.type)) return;

    setChartData([]);
    setExcelExportStatus(false);

    setGetChartLoading(true);
    allCharts({
      device_id: chartInfo.device_id,
      start_date: startDate,
      end_date: endDate,
      first_index: chartInfo.first_index,
      count: chartInfo.count,
      chart_num: chartInfo.chart_num,
      coefficients: chartInfo.coefficients,
      bit: chartInfo.bit ?? null,
    })
      .then(({ data }) => {
        if (data.chart_data.status == false) {
          return ErrorToast(data.chart_data.message);
        }
        const chart_data = data.chart_data;
        if (
          !chart_data.series ||
          !chart_data.xData ||
          chart_data.xData.length == 0
        ) {
          return ErrorToast("داده ای برای نمایش وجود ندارد");
        }

        const colors = [
          "#FF5733",
          "#33FF57",
          "#3357FF",
          "#F39C12",
          "#8E44AD",
          "#16A085",
          "#E74C3C",
          "#2ECC71",
          "#3498DB",
          "#E67E22",
          "#1ABC9C",
          "#9B59B6",
          "#34495E",
          "#27AE60",
          "#2980B9",
          "#D35400",
          "#C0392B",
          "#7F8C8D",
          "#BDC3C7",
          "#F1C40F",
        ];

        const buildSeries = (index, name, colorIndex, chartName = "") => {
          const key = `i${index}`;
          const values = chart_data.series[key] || [];

          const merged = chart_data.xData.map((ts, i) => [
            Number(ts) * 1000,
            values[i] ?? null,
          ]);

          return {
            name,
            data: merged,
            color: colors[colorIndex % colors.length],
            chartName,
          };
        };

        if (chartInfo.type == 1) {
          if (chartInfo.device_type == 16) {
            const mergedSeries = [];

            for (
              let i = chartInfo.first_index;
              i <= Number(chartInfo.first_index) + Number(chartInfo.count) - 1;
              i++
            ) {
              mergedSeries.push(
                buildSeries(
                  i,
                  chartInfo.names[i - 1],
                  i - chartInfo.first_index,
                ),
              );
            }

            setChartData([mergedSeries]);
          } else {
            const result = [];
            for (
              let i = chartInfo.first_index;
              i <= Number(chartInfo.first_index) + Number(chartInfo.count) - 1;
              i++
            ) {
              result.push([
                buildSeries(
                  i,
                  chartInfo.names[i - 1],
                  i - chartInfo.first_index,
                ),
              ]);
            }

            setChartData(result);
          }
        } else if (chartInfo.type == 2) {
          const deviceCharts = DeviceCharts.find(
            (d) => d.type == chartInfo.device_type,
          );

          const groupedCharts = [];

          deviceCharts.chart_index.forEach(([start, end], index) => {
            const chartGroup = [];
            let nameIndex = 1;

            for (let j = start; j <= end; j++) {
              const key = `i${j}`;
              if (chart_data.series[key]) {
                chartGroup.push(
                  buildSeries(
                    j,
                    nameIndex,
                    j - start,
                    deviceCharts.charts[index],
                  ),
                );
              }
              nameIndex++;
            }

            groupedCharts.push(chartGroup);
          });

          setChartData(groupedCharts);
        }

        setExcelExportStatus(true);
      })
      .finally(() => setGetChartLoading(false));
  };

  const options = useMemo(() => {
    return chartData.map((data, index) => {
      let tickposition = null;
      let yAxisFormatter = function () {
        return this.value;
      };
      let chartHeight = 400;

      const setBinaryFormatter = () => {
        tickposition = [0, 1];
        yAxisFormatter = function () {
          if (this.value == 0) return "قطع";
          if (this.value == 1) return "وصل";
          return this.value;
        };
        chartHeight = 200;
      };

      const isBinaryChart = () => {
        if (chartInfo.type == 1) {
          if (chartInfo.device_type == 16 && chartInfo.bit == 1) {
            return true;
          } else {
            const device = devices.find((d) => d.id == chartInfo.device_id);
            if (!device) return false;
            const deviceChart = DeviceCharts.find((d) => d.type == device.type);
            if (!deviceChart) return false;

            const chartTypeIndex = deviceChart.chart_index.findIndex(
              ([start, end]) =>
                chartInfo.first_index >= start && chartInfo.first_index <= end,
            );
            if (chartTypeIndex == -1) return false;

            const chartType = deviceChart.charts_type[chartTypeIndex];
            return chartType == 1;
          }
        } else if (chartInfo.type == 2) {
          const deviceChart = DeviceCharts.find(
            (d) => d.type == chartInfo.device_type,
          );
          if (!deviceChart) return false;
          if (index == undefined || index >= deviceChart.charts_type.length)
            return false;

          const chartType = deviceChart.charts_type[index];
          return chartType == 1;
        }
        return false;
      };

      if (isBinaryChart()) {
        setBinaryFormatter();
      }

      return {
        time: {
          useUTC: true,
        },
        chart: {
          type: "line",
          zoomType: "x",
          style: {
            fontFamily: "primary-font",
            fontSize: "1em",
            fontWeight: "lighter",
          },
          height: chartHeight,
          marginBottom: 100,
        },
        title: {
          text:
            chartInfo.type == 2
              ? DeviceCharts.find((d) => d.type == chartInfo.device_type)
                  .charts[index]
              : chartInfo.type == 1
                ? ""
                : "",
          style: {
            fontWeight: "lighter",
            fontSize: "1em",
          },
        },
        tooltip: {
          formatter: function () {
            const persianDate = new PersianDate(this.x)
              .toCalendar("persian")
              .utc();
            const jDate = persianDate.format("YYYY/MM/DD HH:mm:ss");

            let valueDisplay = this.y;
            if (isBinaryChart()) {
              if (this.y == 0) valueDisplay = "قطع";
              else if (this.y == 1) valueDisplay = "وصل";
            }

            return `<b>${this.series.name}</b><br/>${jDate}<br/>${valueDisplay}`;
          },
        },
        xAxis: {
          type: "datetime",
          dateTimeLabelFormats: {
            millisecond: "%H:%M:%S.%L",
            second: "%H:%M:%S",
            minute: "%H:%M",
            hour: "%H:%M",
            day: "%e %b",
            week: "%e %b",
            month: "%b '%y",
            year: "%Y",
          },
        },
        yAxis: {
          title: {
            text: "",
          },
          tickPositions: tickposition,
          labels: {
            formatter: yAxisFormatter,
          },
          maxPadding: 0,
          minPadding: 0,
        },
        series: data,
        plotOptions: {
          series: {
            connectNulls: true,
          },
        },
      };
    });
  }, [chartData, startDate, endDate]);

  // const tooltipFormatter = (point) => {
  //   const formattedTime = point.x;
  //   return `<div class="text-start" dir="ltr">
  //           تاریخ: ${point.series.name}<br/>
  //           زمان: ${new Date(formattedTime).toLocaleDateString()}<br/>`;
  // };

  return (
    // Modal
    <div
      className="modal fade chart-modal"
      id="defineChartModal"
      tabIndex="-1"
      aria-labelledby="changeNameModal"
      aria-hidden="true"
      ref={modalName}
    >
      <style>
        {`
          .highcharts-credits {
            display: none !important;
          }
          .modal-xl {
            --bs-modal-width: 100%;
            max-width: 1200px;
          }

          @media (max-width: 767.98px) {
            .modal .modal-dialog:not(.modal-fullscreen) {
              padding: 0;
              padding-left: 0 !important;
              padding-left: 0;
              margin: 1.5rem auto;
            }
          }
        `}
      </style>
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">نمودار</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              ref={closeButton}
            ></button>
          </div>
          <div className="modal-body mx-0 px-0">
            <div className="row px-0 mx-0">
              <div className="col-12 mb-3">
                <DatePickerRange
                  placeholder="بازه زمانی را انتخاب کنید"
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                />
              </div>
              <div className="col-12 text-end mb-3">
                <div className="d-flex justify-content-between">
                  {excelExportStatus ? (
                    <ChartExport
                      data={chartData}
                      fileName="Chart"
                      title="خروجی اکسل"
                      btnClassName="btn btn-success-app"
                    />
                  ) : (
                    <></>
                  )}
                  <button
                    type="button"
                    className="btn btn-show-chart btn-success-app mx-3"
                    onClick={handleShowChart}
                  >
                    {getChartLoading ? (
                      <span
                        className="spinner-border"
                        role="status"
                        aria-hidden="true"
                      ></span>
                    ) : (
                      <span>نمایش</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="charts-container">
              {chartData.map((chart, index) => (
                <div key={index} className="chart-card">
                  {/* {options[index]?.title?.text && (
                    <div className="chart-title">
                      {options[index].title.text}
                    </div>
                  )} */}
                  <HighchartsReact
                    ref={chartComponentRef}
                    highcharts={Highcharts}
                    options={options[index]}
                  />
                  <hr
                    className="m-0 p-0"
                    style={{ borderColor: "rgba(0,165,148,0.1)" }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartModal;
